import type { IIngredient, IPack, IProduct, IProductVariation } from '@app/common';
import { isPackOutOfStock, isProductOutOfStock, isProductVariationOutOfStock } from '@tickeat/catalog';
import type { IPack as IPackCatalogue, IProduct as IProduitCatalogue } from '@tickeat/catalog';

// LES RUPTURES DEPUIS LE DASHBOARD (Maxence, 1er nov. 2026 : « il faut que les
// restaurants, s'ils se connectent sur le dashboard, puissent désactiver un
// ingrédient pour le mettre en rupture, comme sur l'OS »). La rupture est
// portée par l'ingrédient, restaurant par restaurant ; produits, variations et
// menus suivent PAR RICOCHET, avec les fonctions partagées de
// `@tickeat/catalog` — celles de la caisse, de l'eshop et de HubRise. Module
// PUR : il ne connaît ni le store ni le réseau.

export function estEnRupture(ingredient: Pick<IIngredient, 'ingredientShops'>, shopId: string): boolean {
  return ingredient.ingredientShops?.some((etat) => etat.shopId === shopId && etat.outOfStock) ?? false;
}

const parNom = (a: Pick<IIngredient, 'name'>, b: Pick<IIngredient, 'name'>) =>
  a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });

export function ingredientsEnRupture<I extends Pick<IIngredient, 'name' | 'ingredientShops'>>(ingredients: I[], shopId: string): I[] {
  return ingredients.filter((ingredient) => estEnRupture(ingredient, shopId)).sort(parNom);
}

const sansAccents = (texte: string): string =>
  texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function correspondALaRecherche(ingredient: Pick<IIngredient, 'name'>, recherche: string): boolean {
  const cherche = sansAccents(recherche);
  return cherche === '' || sansAccents(ingredient.name).includes(cherche);
}

export interface VariationDesactivee {
  produit: IProduct;
  variation: IProductVariation;
}

export interface Ricochet {
  /** Produits dont toutes les variations sont touchées. */
  produits: IProduct[];
  /** Variations touchées d'un produit qui en garde d'autres servables. */
  variations: VariationDesactivee[];
  /** Menus dont une étape obligatoire n'a plus aucune variation servable. */
  menus: IPack[];
}

export function desactivesParRicochet(
  catalogue: { products: IProduct[]; packs: IPack[] },
  idsEnRupture: string[],
): Ricochet {
  const produits = catalogue.products.filter((product) =>
    isProductOutOfStock(product as unknown as IProduitCatalogue, idsEnRupture),
  );
  const touches = new Set(produits.map((p) => p._id));

  const variations = catalogue.products
    .filter((product) => !touches.has(product._id))
    .flatMap((produit) =>
      (produit.productVariations ?? [])
        .filter((variation) => isProductVariationOutOfStock(variation as never, idsEnRupture))
        .map((variation) => ({ produit, variation })),
    );

  const menus = catalogue.packs.filter((pack) => isPackOutOfStock(pack as unknown as IPackCatalogue, idsEnRupture));

  return { produits, variations, menus };
}
